import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Unlock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HiddenGate() {
  const { slug } = useParams();
  const [key, setKey] = useState("");
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!key) return;
    setLoading(true);
    setError(false);
    try {
      const res = await axios.post(`${API}/verify-key`, { slug, decryption_key: key });
      setContent(res.data);
      toast.success("DECRYPTION SUCCESSFUL");
    } catch (err) {
      setError(true);
      toast.error("DECRYPTION FAILED");
    } finally {
      setLoading(false);
    }
  };

  if (content) {
    return (
      <div data-testid="hidden-page-content" className="min-h-screen bg-[#050505] noise-bg flex items-start justify-start p-6 sm:p-12 md:p-20">
        <div className="max-w-xl">
          <div className="flex items-center gap-3 mb-8">
            <Unlock size={14} className="text-[#00FF00]/70" />
            <span className="text-[10px] tracking-[0.25em] text-[#00FF00]/60 uppercase font-bold" style={{ fontFamily: "'Azeret Mono', monospace" }}>
              signal.decrypted // access_granted
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#E5E5E5] mb-6" style={{ fontFamily: "'Azeret Mono', monospace" }}>
            {content.title}
          </h1>
          <div className="text-sm text-[#888] leading-relaxed whitespace-pre-wrap pl-4 border-l border-[#00FF00]/20" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {content.content}
          </div>
          <div className="mt-10">
            <p className="text-[9px] text-[#333] tracking-[0.15em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              page.type = {content.page_type} // slug = {content.slug}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="hidden-gate-page" className="min-h-screen bg-[#050505] noise-bg flex items-start justify-start p-6 sm:p-12 md:p-20">
      <div className="max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <Lock size={14} className="text-[#FF3333]/70" />
          <span className="text-[10px] tracking-[0.25em] text-[#FF3333]/60 uppercase font-bold" style={{ fontFamily: "'Azeret Mono', monospace" }}>
            sys.gate // restricted_frequency
          </span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#E5E5E5] mb-2" style={{ fontFamily: "'Azeret Mono', monospace" }}>
          DECRYPTION REQUIRED
        </h1>
        <p className="text-xs text-[#555] mb-8 leading-relaxed">
          this signal is locked. if you have the key, enter it below. if you don't, this page doesn't exist for you.
        </p>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] text-[#555] uppercase font-bold block mb-2" style={{ fontFamily: "'Azeret Mono', monospace" }}>
              &gt; decryption.key
            </label>
            <Input
              data-testid="hidden-page-password-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="enter key"
              className={`bg-transparent border-white/10 rounded-none text-sm text-[#E5E5E5] placeholder:text-[#222] sys-input h-11 ${
                error ? "border-[#FF3333]/50" : ""
              }`}
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            />
            {error && (
              <p className="text-[10px] text-[#FF3333]/70 mt-2 flicker">
                &gt; decryption_failed // key_invalid
              </p>
            )}
          </div>

          <button
            data-testid="hidden-page-submit-button"
            type="submit"
            disabled={loading}
            className="border border-white/20 bg-transparent text-[#E5E5E5] px-8 py-4 text-[10px] tracking-[0.25em] uppercase font-bold sys-btn disabled:opacity-30"
            style={{ fontFamily: "'Azeret Mono', monospace" }}
          >
            {loading ? "decrypting..." : "[ ATTEMPT DECRYPTION ]"}
          </button>
        </form>

        <p className="text-[9px] text-[#222] mt-8 tracking-[0.1em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          signal.ref: /{slug} // page.indexed = false
        </p>
      </div>
    </div>
  );
}
